import { Dispatch, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import {
  Criteria_Config_Set_Input,
  Criteria_Group_Config_Set_Input,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  CriteriaConfigurationCriteriaGroupsForm,
  CriteriaGroupFormValues,
} from 'src/screens/administration/configuration-management/criteria-configuration/criteria-group/edit/CriteriaConfigurationCriteriaGroupsForm'
import { CRITERIA_CONFIG_PROCESSES } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import {
  queryCriteriaGroupById,
  updateCriteriaGroupConfigAndRelatedEvaluationCriteriaByGroupId,
} from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { OperationResult, useClient } from 'urql'
import { resolveCriteriaConfigRoot } from 'src/routing/routing-utils'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

type UpdateCriteriaGroupResponse =
  | OperationResult<{ update_criteria_group_config: Mutation_Root['update_criteria_group_config'] }>
  | OperationResult<{
      update_criteria_group_config: Mutation_Root['update_criteria_group_config']
      update_criteria_config: Mutation_Root['update_criteria_config']
    }>

const submitCriteriaGroupConfigForm = () => {
  document.getElementById('criteria-group-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const CriteriaConfigurationCriteriaGroupsEditPage = () => {
  const { groupId } = useParams()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()
  const { state } = useLocation()

  const [initialValues, setInitialValues] = useState<CriteriaGroupFormValues | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const onBackHandler = useCallback(() => {
    const processTypeFromState = state?.processType

    if (processTypeFromState !== null && processTypeFromState !== undefined) {
      navigate(resolveCriteriaConfigRoot(processTypeFromState))
    } else {
      navigate(ROUTES.CriteriaConfigurationIndex.nested.CriteriaConfigurationCriteriaGroups.params({}))
    }
  }, [navigate, state])

  const onSave = async () => {
    originRef.current = 'SAVE'
    submitCriteriaGroupConfigForm()
  }

  const onSaveAndBack = async () => {
    originRef.current = 'SAVE_AND_BACK'
    submitCriteriaGroupConfigForm()
  }

  const fetchCriteriaGroupToEdit = useCallback(async () => {
    if (!groupId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{ criteria_group_config: Query_Root['criteria_group_config'] }>(queryCriteriaGroupById, { groupId })
      .toPromise()

    if (error || !data?.criteria_group_config[0]) {
      throw new Error('Resource not found')
    }

    const fetchedCriteriaGroup = data.criteria_group_config[0]
    const fetchedGroupProcess = fetchedCriteriaGroup.group_processes[0]
    if (!fetchedGroupProcess || !CRITERIA_CONFIG_PROCESSES.includes(fetchedGroupProcess)) {
      throw new Error('Invalid state of fetched criteria group processes')
    }

    const editingCriteriaGroup: CriteriaGroupFormValues = {
      enName: fetchedCriteriaGroup.names['en'],
      deName: fetchedCriteriaGroup.names['de'],
      frName: fetchedCriteriaGroup.names['fr'],
      itName: fetchedCriteriaGroup.names['it'],
      status: fetchedCriteriaGroup.group_active ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE,
      processAlloc: fetchedGroupProcess,
      criteriaTypes: fetchedCriteriaGroup.criteria_types,
    }

    return { editingCriteriaGroup }
  }, [groupId, urqlClient])

  const updateCriteriaGroupToActive = useCallback(
    async (
      groupId: number,
      criteriaGroupFieldsSetClause: Criteria_Group_Config_Set_Input,
    ): Promise<UpdateCriteriaGroupResponse> => {
      const criteriaConfigFieldsSetClause: Criteria_Config_Set_Input = {
        processes: criteriaGroupFieldsSetClause.group_processes,
      }

      return await urqlClient
        .mutation<{
          update_criteria_group_config: Mutation_Root['update_criteria_group_config']
          update_criteria_config: Mutation_Root['update_criteria_config']
        }>(updateCriteriaGroupConfigAndRelatedEvaluationCriteriaByGroupId, {
          groupId,
          criteriaGroupFieldsSetClause,
          criteriaConfigFieldsSetClause,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const updateCriteriaGroupToInactive = useCallback(
    async (
      groupId: number,
      criteriaGroupFieldsSetClause: Criteria_Group_Config_Set_Input,
    ): Promise<UpdateCriteriaGroupResponse> => {
      const criteriaConfigFieldsSetClause: Criteria_Config_Set_Input = {
        processes: criteriaGroupFieldsSetClause.group_processes,
        active: false,
      }

      return await urqlClient
        .mutation<{
          update_criteria_group_config: Mutation_Root['update_criteria_group_config']
          update_criteria_config: Mutation_Root['update_criteria_config']
        }>(updateCriteriaGroupConfigAndRelatedEvaluationCriteriaByGroupId, {
          groupId,
          criteriaGroupFieldsSetClause,
          criteriaConfigFieldsSetClause,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const handleSubmitLocal = useCallback(
    async (values: CriteriaGroupFormValues, setFormDirty: Dispatch<boolean>) => {
      const isActiveSelected = values.status == CONFIGURATION_STATUS.ACTIVE

      const criteriaGroupFieldsSetClause: Criteria_Group_Config_Set_Input = {
        names: { de: values.deName, en: values.enName, fr: values.frName, it: values.itName },
        group_active: isActiveSelected,
      }

      let updateCriteriaGroupResponse: UpdateCriteriaGroupResponse
      const groupIdParsed = parseInt(groupId as string)
      if (isActiveSelected) {
        updateCriteriaGroupResponse = await updateCriteriaGroupToActive(groupIdParsed, criteriaGroupFieldsSetClause)
      } else {
        updateCriteriaGroupResponse = await updateCriteriaGroupToInactive(groupIdParsed, criteriaGroupFieldsSetClause)
      }

      const { data, error } = updateCriteriaGroupResponse
      if (!error && data?.update_criteria_group_config?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        setFormDirty(false)
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [groupId, notificationService, onBackHandler, updateCriteriaGroupToActive, updateCriteriaGroupToInactive],
  )

  useEffect(() => {
    fetchCriteriaGroupToEdit()
      .then((res) => {
        setInitialValues(res.editingCriteriaGroup)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchCriteriaGroupToEdit, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.criteria.group.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <CriteriaConfigurationCriteriaGroupsForm
              mode="EDIT"
              onSubmit={handleSubmitLocal}
              initialValues={initialValues}
            />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
